import { useSiteContent } from '@/hooks/useSiteContent';
import { useTranslation } from 'react-i18next';
import ContentState from '@/components/feature/ContentState';

interface TeamMember {
  name: string;
  role: string;
  image: string;
}

export default function TeamSection() {
  const { data, loading, error, refresh } = useSiteContent<TeamMember[]>('about_team_members');
  const { t } = useTranslation();

  if (loading || error || !data) {
    return <ContentState loading={loading} error={error} ready={!!data} onRetry={refresh} />;
  }

  return (
    <section className="w-full bg-background-200 py-14 md:py-20">
      <div className="w-full px-4 md:px-8 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-14">
          <p className="text-accent-500 font-body text-sm tracking-wide uppercase mb-2">{t('about.teamEyebrow')}</p>
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-secondary-900 leading-tight">
            {t('about.teamTitleA')} <span className="text-accent-500">{t('about.teamTitleB')}</span>
          </h2>
          <p className="text-secondary-500 text-sm md:text-base mt-3 max-w-xl mx-auto">
            {t('about.teamSubtitle')}
          </p>
        </div>

        {/* Team Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {data.map((member) => (
            <div key={member.name} className="text-center group">
              <div className="relative w-40 h-40 md:w-48 md:h-48 mx-auto mb-4 rounded-full overflow-hidden">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 rounded-full border-2 border-transparent group-hover:border-accent-500/40 transition-colors" />
              </div>
              <h3 className="font-heading text-base md:text-lg font-semibold text-secondary-900">
                {member.name}
              </h3>
              <p className="text-secondary-500 text-sm">{member.role}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}